/**
 * 视口单位兜底脚本（docs/DEVICES.md R3）。
 *
 * 要解决的是同一个病的两种症状：
 *
 *  1. **`100vh` 含被工具栏遮住的那一段。** 微信 WKWebView 里 `innerHeight` 是
 *     「连导航条一起算」的高度，`100vh` 按它算，于是 fixed 布局的底部操作区
 *     有一截被压在微信导航条底下 —— 看得见、点不到。「弃牌/跟注」点不到 = 这局废了。
 *     有 `dvh` 的浏览器 CSS 自己能解决；没有的（老 Android WebView / iOS 15 以下）
 *     只能靠 JS 把 `visualViewport.height` 写进 `--vh` / `--app-h`。
 *
 *  2. **被遮住的那一截不在 `env(safe-area-inset-bottom)` 里。** 安全区只描述
 *     屏幕形状（Home 指示条、刘海），不描述浏览器自己画的 UI。
 *     所以哪怕 `dvh` 可用，也要把 `innerHeight - visualViewport.height - offsetTop`
 *     量出来写进 `--toolbar-guard`，让 `.dock` 一起让开。
 *
 * 纯函数 `computeViewportUnits` 与副作用 `installViewportUnits` 分开：
 * 前者可以在 node 里逐个像素地断言，后者只负责接线，测试用假 window 注入。
 */

// 同一个模块里带上样式：任何用到视口兜底的入口都会自动把 viewport.css 打进包，
// 不会出现「脚本装了、CSS 忘了 import」这种只在真机上才暴露的半截适配。
import './viewport.css'
import { isKeyboardLocked } from './keyboardLock.js'

/** 量出来的「被浏览器 UI 遮住的底部高度」超过这个值就当作是软键盘，忽略。
 *  软键盘可以顶掉半个屏幕，按它给 dock 加 padding 会把整排按钮挤没。 */
export const MAX_TOOLBAR_GUARD_PX = 96

/** 小于这个值的差额当作测量噪声（缩放、亚像素）丢掉 */
const TOOLBAR_GUARD_EPSILON = 2

export interface ViewportMeasurement {
  /** window.innerHeight —— 含浏览器 UI 的「布局视口」高度 */
  readonly innerHeight: number
  /** visualViewport.height —— 真正看得见的高度；没有 visualViewport 时传 undefined */
  readonly visualHeight?: number | undefined
  /** visualViewport.offsetTop —— 页面被上方 UI 推下去的量 */
  readonly visualOffsetTop?: number | undefined
  /** visualViewport.scale —— 用户捏合放大时不要改布局（红线 #56：缩放必须可用） */
  readonly scale?: number | undefined
}

export interface ViewportUnits {
  /** 可视高度（px），写进 --app-h */
  readonly appH: number
  /** 1vh 对应的像素数，写进 --vh */
  readonly vh: number
  /** 底部被浏览器 UI 遮住的高度（px），写进 --toolbar-guard */
  readonly toolbarGuard: number
}

/**
 * 纯计算。三条规则：
 *  - 没有 visualViewport（老 WebView）→ 只能信 innerHeight，guard 记 0。
 *  - 用户捏合放大（scale > 1）→ visualViewport 变成「放大镜取景框」，
 *    此时它的高度与布局无关，一律按 innerHeight 走，否则一放大整个壳层就塌了。
 *  - guard 超过 MAX_TOOLBAR_GUARD_PX 判定为软键盘，记 0。
 */
export function computeViewportUnits(m: ViewportMeasurement): ViewportUnits {
  const inner = Math.max(0, Math.round(m.innerHeight))
  const scale = m.scale ?? 1
  const visual = m.visualHeight
  const zoomed = scale > 1.01
  const usable =
    visual === undefined || !Number.isFinite(visual) || visual <= 0 || zoomed
      ? inner
      : Math.max(0, Math.round(visual))

  let guard = 0
  if (!zoomed && visual !== undefined && Number.isFinite(visual) && visual > 0) {
    const hidden = inner - (visual + (m.visualOffsetTop ?? 0))
    if (hidden > TOOLBAR_GUARD_EPSILON && hidden <= MAX_TOOLBAR_GUARD_PX) guard = Math.round(hidden)
  }

  return { appH: usable, vh: usable / 100, toolbarGuard: guard }
}

// ============================================================================
// 接线
// ============================================================================

/** 只依赖用得到的那几个成员，测试里给个对象字面量就能跑 */
export interface ViewportUnitsHost {
  readonly innerHeight: number
  readonly visualViewport?: {
    readonly height: number
    readonly offsetTop: number
    readonly scale: number
    addEventListener(type: string, fn: () => void): void
    removeEventListener(type: string, fn: () => void): void
  } | null
  addEventListener(type: string, fn: () => void): void
  removeEventListener(type: string, fn: () => void): void
}

export interface ViewportUnitsTarget {
  setProperty(name: string, value: string): void
}

export interface InstallViewportUnitsOptions {
  readonly host?: ViewportUnitsHost | null
  /** 写 CSS 变量的目标，默认 document.documentElement.style */
  readonly target?: ViewportUnitsTarget | null
  /** rAF 节流；测试传同步版本。返回取消函数。 */
  readonly schedule?: (fn: () => void) => () => void
}

function defaultSchedule(fn: () => void): () => void {
  if (typeof requestAnimationFrame !== 'function') {
    fn()
    return () => {}
  }
  const id = requestAnimationFrame(fn)
  return () => cancelAnimationFrame(id)
}

function domTarget(): ViewportUnitsTarget | null {
  return globalThis.document?.documentElement?.style ?? null
}

/**
 * 装上兜底。返回卸载函数（HMR / 测试用）。
 *
 * 事件选择：`resize` 覆盖工具栏收放与折叠机展开；`orientationchange` 覆盖转屏；
 * `pageshow` 覆盖 iOS 从 bfcache 回来（回来时 visualViewport 是旧值，必须重量一次）。
 * **不监听 scroll**：iOS 滚动中 visualViewport 每帧都在变，跟着写等于每帧重排整个壳层。
 */
export function installViewportUnits(options: InstallViewportUnitsOptions = {}): () => void {
  const host = options.host ?? (globalThis as unknown as ViewportUnitsHost | undefined) ?? null
  const target = options.target ?? domTarget()
  const schedule = options.schedule ?? defaultSchedule
  if (host === null || target === null) return () => {}

  let cancel: (() => void) | null = null
  let pending = false

  const write = (): void => {
    pending = false
    cancel = null
    // 打字期间键盘占掉的那一截不是工具栏：不写，解锁时 keyboardLock 会补发一次 resize
    if (isKeyboardLocked()) return
    const vv = host.visualViewport ?? null
    const units = computeViewportUnits({
      innerHeight: host.innerHeight,
      visualHeight: vv?.height,
      visualOffsetTop: vv?.offsetTop,
      scale: vv?.scale,
    })
    target.setProperty('--vh', `${units.vh}px`)
    target.setProperty('--app-h', `${units.appH}px`)
    target.setProperty('--toolbar-guard', `${units.toolbarGuard}px`)
  }

  const run = (): void => {
    if (pending) return
    pending = true
    cancel = schedule(write)
  }

  write()

  const vv = host.visualViewport ?? null
  host.addEventListener('resize', run)
  host.addEventListener('orientationchange', run)
  host.addEventListener('pageshow', run)
  vv?.addEventListener('resize', run)

  return () => {
    if (cancel !== null) cancel()
    cancel = null
    pending = false
    host.removeEventListener('resize', run)
    host.removeEventListener('orientationchange', run)
    host.removeEventListener('pageshow', run)
    vv?.removeEventListener('resize', run)
  }
}

/**
 * 把形态档写到 `<html data-viewport>` 上，让 viewport.css 自己切版面。
 *
 * ★ 换档**只改这一个属性**，绝不切组件树（R1）：一旦按档位换 key / 换组件，
 * 折叠机展开的那一瞬间 RaiseControl 会被卸载重建，玩家没提交的加注滑块位置
 * 连同 liveRef 一起没了 —— 手指还按在屏幕上，金额已经跳回最小加注。
 */
export function applyViewportClass(klass: string, el?: { dataset: Record<string, string> } | null): void {
  const node = el ?? (globalThis.document?.documentElement as unknown as { dataset: Record<string, string> } | undefined)
  if (!node?.dataset) return
  node.dataset['viewport'] = klass
}
